import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import {
    createDropdown,
    View,
    LabeledFieldView,
    createLabeledInputText,
    ButtonView
} from '@ckeditor/ckeditor5-ui';
import submitHandler from '@ckeditor/ckeditor5-ui/src/bindings/submithandler.js';
import { icons } from '@ckeditor/ckeditor5-core';
import multimediaIcon from '../theme/icons/multimedia.svg';

export default class MultiMediaEmbedUI extends Plugin {
    static get pluginName() {
        return 'MultiMediaEmbedUI';
    }

    init() {
        const editor = this.editor;
        if (editor.plugins.has('MediaEmbed')) {
            const config = editor.config.get('mediaEmbed') || {};

            config.providers = (config.providers || []).concat([
                {
                    name: 'spotify',
                    // Patrones simplificados y robustos
                    url: [
                        /^(https?:\/\/)?open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(track|album|playlist|artist|episode|show)\/([\w-]+)/
                    ],
                    html: match => {
                        const type = match[2];
                        const id = match[3];
                        const embedUrl = `https://open.spotify.com/embed/${type}/${id}`; 
                        
                        return (
                            `<div class="ck-media-iframe-container" style="position:relative;height:352px;">` +
                            `<iframe style="border-radius:12px" ` +
                            `src="${embedUrl}" ` + 
                            `width="100%" height="100%" frameborder="0" ` +
                            `allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>` +
                            `</div>`
                        );
                    }
                },
                {
                    name: 'vimeo',
                    url: [
                        /^(https?:\/\/)?(www\.)?vimeo\.com\/(\d+)/,
                        /^(https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/
                    ],
                    html: match => {
                        const id = match[3] || match[4]; 
                        return (
                            `<div class="ck-media-iframe-container" style="position:relative;padding-bottom:56.25%;height:0;">` +
                            `<iframe src="https://player.vimeo.com/video/${id}" ` +
                            `style="position:absolute;width:100%;height:100%;top:0;left:0;border:0;" ` +
                            `allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>` +
                            `</div>`
                        );
                    }
                }
            ]);

            editor.config.set('mediaEmbed', config);
        }
        
        editor.ui.componentFactory.add('multiMediaEmbed', locale => {
            const dropdown = createDropdown(locale);
            const button = dropdown.buttonView;

            button.set({
                label: 'Insertar multimedia',
                icon: multimediaIcon,
                tooltip: true
            });

            // Crear formulario
            const formView = this._createFormView(locale);

            dropdown.panelView.children.add(formView);

            // Foco automático al abrir
            dropdown.on('change:isOpen', (evt, name, isOpen) => {
                if (isOpen) {
                    formView.urlInput.fieldView.value = '';
                    setTimeout(() => {
                        formView.urlInput.fieldView.element.focus();
                    }, 0);
                }
            });

            // Eventos del formulario
            this.listenTo(formView, 'submit', () => {
                const inputElement = formView.urlInput.fieldView.element;
                const url = inputElement ? inputElement.value.trim() : '';

                if (!url.length) {
                    console.warn('URL vacía');
                    return;
                }

                this._insertByUrl(editor, url);
                dropdown.isOpen = false;
            });

            this.listenTo(formView, 'cancel', () => {
                dropdown.isOpen = false;
            });

            return dropdown;
        });
    }

    /**
     * Crea el formulario con estilo MediaEmbed
     */
    _createFormView(locale) {
        const t = locale.t;

        class MediaFormView extends View {
            constructor(locale) {
                super(locale);

                // Input para la URL
                this.urlInput = new LabeledFieldView(locale, createLabeledInputText);
                this.urlInput.label = t('Media URL');
                this.urlInput.infoText = t('Paste the media URL in the input.');
                this.urlInput.fieldView.placeholder = 'https://';

                // Botón Save con ícono checkmark
                this.saveButton = new ButtonView(locale);
                this.saveButton.set({
                    label: t('Save'),
                    icon: icons.check,
                    tooltip: true,
                    class: 'ck-button-save',
                    type: 'submit'
                });

                // Botón Cancel con ícono X
                this.cancelButton = new ButtonView(locale);
                this.cancelButton.set({
                    label: t('Cancel'),
                    icon: icons.cancel,
                    tooltip: true,
                    class: 'ck-button-cancel'
                });

                // Eventos de botones
                this.listenTo(this.saveButton, 'execute', () => {
                    this.fire('submit');
                });

                this.listenTo(this.cancelButton, 'execute', () => {
                    this.fire('cancel');
                });

                // Template del formulario
                this.setTemplate({
                    tag: 'form',
                    attributes: {
                        class: ['ck', 'ck-media-form', 'ck-responsive-form'],
                        tabindex: '-1'
                    },
                    children: [
                        this.urlInput,
                        this.saveButton,
                        this.cancelButton
                    ]
                });
            }

            render() {
                super.render();
                // Configurar submit handler
                submitHandler({ view: this });
            }
        }

        return new MediaFormView(locale);
    }

    /**
     * Inserta el contenido según la URL detectada
     */
    _insertByUrl(editor, url) {
        if (!url) return;

        try {
            // Detectar tipo de contenido
            if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i.test(url)) {
                this._insertImage(editor, url);
            }
            else if (/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(url)) {
                this._insertAudio(editor, url);
            }
            else if (/\.(mp4|webm|mov|avi|mkv|ogv)$/i.test(url)) {
                this._insertVideo(editor, url);
            }
            else {
                // Para URLs de Spotify, YouTube, etc.
                this._insertMediaEmbed(editor, url); 
            }

            editor.editing.view.focus();
        } catch (error) {
            console.error('Error al insertar:', error);
        }
    }

    _insertImage(editor, url) {
        if (editor.commands.get('insertImage')) {
            editor.execute('insertImage', { source: url });
        } else if (editor.commands.get('imageInsert')) {
            editor.execute('imageInsert', { source: url });
        } else {
            editor.model.change(writer => {
                const imageElement = writer.createElement('imageBlock', { src: url });
                editor.model.insertContent(imageElement, editor.model.document.selection);
            });
        }
    }

    _insertAudio(editor, url) {
        if (editor.model.schema.isRegistered('recorderBlock')) {
            editor.model.change(writer => {
                const recorderElement = writer.createElement('recorderBlock', {
                    type: 'audio',
                    src: url
                });
                editor.model.insertContent(recorderElement, editor.model.document.selection);
            });
        } else if (editor.commands.get('htmlEmbed')) {
            const audioHtml = `<audio controls style="width: 100%; max-width: 500px;">
                <source src="${url}" type="audio/${this._getAudioType(url)}">
            </audio>`;
            editor.execute('htmlEmbed', { value: audioHtml });
        } else {
            const audioHtml = `<audio controls style="width: 100%; max-width: 500px;">
                <source src="${url}" type="audio/${this._getAudioType(url)}">
            </audio>`;
            this._insertRawHtml(editor, audioHtml);
        }
    }

    _insertVideo(editor, url) {
        if (editor.commands.get('insertVideo')) {
            editor.execute('insertVideo', { source: url });
        } else if (editor.model.schema.isRegistered('recorderBlock')) {
            editor.model.change(writer => {
                const recorderElement = writer.createElement('recorderBlock', {
                    type: 'video',
                    src: url
                });
                editor.model.insertContent(recorderElement, editor.model.document.selection);
            });
        } else if (editor.commands.get('htmlEmbed')) {
            const videoHtml = `<video controls style="width: 100%; max-width: 100%;">
                <source src="${url}" type="video/${this._getVideoType(url)}">
            </video>`;
            editor.execute('htmlEmbed', { value: videoHtml });
        } else {
            const videoHtml = `<video controls style="width: 100%; max-width: 100%;">
                <source src="${url}" type="video/${this._getVideoType(url)}">
            </video>`;
            this._insertRawHtml(editor, videoHtml);
        }
    }

    /**
     * Usa el comando nativo 'mediaEmbed' de CKEditor 5.
     * Esta es la lógica recomendada.
     */
    _insertMediaEmbed(editor, url) {
        const command = editor.commands.get('mediaEmbed');

        if (command && command.isEnabled) {
            try {
                // Esto ejecuta el comando que usa la configuración inyectada en init()
                command.execute(url);
            } catch (error) {
                console.error('Error al ejecutar mediaEmbed:', error);
            }
        } else {
            // Fallback para videos o iframes que no son Spotify/Vimeo/etc.
            this._insertUnrecognizedUrl(editor, url);
        }
    }

    /**
     * Intentar insertar HTML si mediaEmbed no funciona y no es una URL de archivo multimedia.
     * Este es el último recurso (anteriormente parte de _insertMediaEmbed).
     */
    _insertUnrecognizedUrl(editor, url) {
        const command = editor.commands.get('htmlEmbed');
        const iframeHtml = this._createGenericIframe(url);
        
        if (command && command.isEnabled) {
            editor.execute('htmlEmbed', { value: iframeHtml });
        } else {
            console.warn('URL no reconocida. Ni mediaEmbed ni htmlEmbed están disponibles para insertar el contenido.');
        }
    }

    // Nota: La función _getMediaHtml ha sido ELIMINADA y su lógica trasladada a this.init()

    _insertRawHtml(editor, htmlContent) {
        editor.model.change(writer => {
            const viewFragment = editor.data.processor.toView(htmlContent);
            const modelFragment = editor.data.toModel(viewFragment);
            editor.model.insertContent(modelFragment, editor.model.document.selection);
        });
    }
    
    // Función auxiliar para crear un iframe genérico
    _createGenericIframe(url) {
        return `<iframe src="${url}" style="width: 100%; height: 352px; border: 1px solid #ccc;" allowfullscreen></iframe>`;
    }

    _getAudioType(url) {
        if (url.endsWith('.mp3')) return 'mpeg';
        if (url.endsWith('.ogg')) return 'ogg';
        if (url.endsWith('.wav')) return 'wav';
        if (url.endsWith('.m4a')) return 'mp4';
        if (url.endsWith('.aac')) return 'aac';
        if (url.endsWith('.flac')) return 'flac';
        return 'mpeg';
    }

    _getVideoType(url) {
        if (url.endsWith('.mp4')) return 'mp4';
        if (url.endsWith('.webm')) return 'webm';
        if (url.endsWith('.ogv')) return 'ogg';
        if (url.endsWith('.mov')) return 'mp4';
        return 'mp4';
    }
}